import React from "react";

export function getOpenAIClient() {
    return null;
}

export function normalizeTimezoneId(rawTz) {
    const fallback = "America/Los_Angeles";
    const value = String(rawTz || "").trim();
    if (!value) return fallback;

    const compact = value.replace(/\s+/g, "");
    const candidate = compact
        .replace(/^americas\//i, "America/")
        .replace(/^america\//i, "America/");

    try {
        Intl.DateTimeFormat(undefined, { timeZone: candidate });
        return candidate;
    } catch (_error) {
        return fallback;
    }
}

export function uniqueNonEmpty(items = []) {
    const out = [];
    items.forEach((item) => {
        const value = String(item || "").trim();
        if (!value) return;
        if (!out.includes(value)) out.push(value);
    });
    return out;
}

export function simplifyBookingCreateError(error) {
    const gqlErrors = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e) => e?.message).filter(Boolean)
        : [];
    const networkErrors = Array.isArray(error?.networkError?.result?.errors)
        ? error.networkError.result.errors.map((e) => e?.message).filter(Boolean)
        : [];
    const raw =
        gqlErrors[0] ||
        networkErrors[0] ||
        error?.message ||
        "Unknown error";

    const lower = String(raw || "").toLowerCase();
    if (lower.includes("timezone")) {
        return "Timezone is invalid. Please update profile timezone, then try again.";
    }
    if (lower.includes("bookingtypeid")) {
        return "Booking type is invalid or inactive. Please select another booking type.";
    }
    if (lower.includes("variable \"$input\" got invalid value")) {
        return "Some booking data is invalid. Please try again with booking type, date, and time.";
    }
    return raw.length > 220 ? `${raw.slice(0, 217)}...` : raw;
}

export function isTimezoneCreateError(error) {
    const gqlErrors = Array.isArray(error?.graphQLErrors)
        ? error.graphQLErrors.map((e) => String(e?.message || "")).join(" ")
        : "";
    const networkErrors = Array.isArray(error?.networkError?.result?.errors)
        ? error.networkError.result.errors.map((e) => String(e?.message || "")).join(" ")
        : "";
    const combined = `${gqlErrors} ${networkErrors} ${String(error?.message || "")}`.toLowerCase();
    return combined.includes("timezone");
}

export function parseBookingTypeChoice(text = "", bookingTypes = []) {
    const value = String(text || "").trim();
    if (!value || !Array.isArray(bookingTypes) || !bookingTypes.length) return null;

    const numeric = value.match(/^(?:type\s*)?#?(\d{1,2})$/i);
    if (numeric) {
        const oneBasedIndex = Number(numeric[1]);
        if (oneBasedIndex >= 1 && oneBasedIndex <= bookingTypes.length) {
            return bookingTypes[oneBasedIndex - 1];
        }
    }

    const lower = value.toLowerCase();
    const normalized = lower.replace(/[^a-z0-9\s]/g, " ").replace(/\s+/g, " ").trim();
    if (!normalized) return null;

    return (
        bookingTypes.find((item) => normalized.includes(String(item?.title || "").toLowerCase())) ||
        bookingTypes.find((item) => String(item?.title || "").toLowerCase().includes(normalized)) ||
        null
    );
}

export function isConfirmBookForMeIntent(text = "") {
    return /^(1|confirm|yes|book it|create it|submit)$/i.test(String(text || "").trim());
}

export function isCancelBookForMeIntent(text = "") {
    return /^(2|cancel|stop|nevermind|abort)$/i.test(String(text || "").trim());
}

export function buildBookingTypeChoicePrompt(bookingTypes = []) {
    if (!bookingTypes.length) {
        return "I could not find any active booking types for this business. Please create a booking type first.";
    }
    const lines = [
        "Tell me which booking type to use. Reply with number or exact name:",
        ...bookingTypes.slice(0, 8).map(
            (item, index) => `${index + 1}. ${item.title} (${item.durationMinutes || 30} min)`
        ),
    ];
    if (bookingTypes.length > 8) {
        lines.push(`+ ${bookingTypes.length - 8} more. You can type the exact booking type name.`);
    }
    return lines.join("\n");
}

export function PlanyBotSparkIcon({ size = 24 }) {
    return (
        <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
            <path
                d="M12 2.8L14.7 9.3L21.2 12L14.7 14.7L12 21.2L9.3 14.7L2.8 12L9.3 9.3L12 2.8Z"
                fill="#ffffff"
            />
            <path
                d="M18.2 4.8L18.9 6.5L20.6 7.2L18.9 7.9L18.2 9.6L17.5 7.9L15.8 7.2L17.5 6.5L18.2 4.8Z"
                fill="#bfdbfe"
            />
        </svg>
    );
}

export function mapAvailableBookingTypes(selectedBusiness) {
    const items = Array.isArray(selectedBusiness?.businessBookingTypes)
        ? selectedBusiness.businessBookingTypes
        : [];
    return items
        .filter((item) => {
            if (!item?.id || !String(item?.title || "").trim()) return false;
            if (item?.isActive === false) return false;
            if (item?.active === false) return false;
            return true;
        })
        .map((item) => ({
            id: item.id,
            title: String(item.title || "").trim(),
            durationMinutes: Number(item.durationMinutes || 30) || 30,
            bookingApproval: Boolean(item.bookingApproval),
            price: Number(item.price || 0) || 0,
            discount: Number(item.discount || 0) || 0,
            link: item.link || "",
            isPublic: Boolean(item.isPublic),
            displayOnBookingPage: Boolean(item.displayOnBookingPage),
            assignedStaffs: Array.isArray(item.assignedStaffs) ? item.assignedStaffs : [],
        }));
}

export function findCurrentLoginStaff(selectedBusiness, providerUser) {
    const staffs = Array.isArray(selectedBusiness?.businessStaffs) ? selectedBusiness.businessStaffs : [];
    const currentStaffId = Number(providerUser?.staffId || 0);
    const currentUserId = Number(providerUser?.userId || providerUser?.id || 0);
    return (
        staffs.find((staff) => Number(staff?.id || 0) === currentStaffId) ||
        staffs.find((staff) => Number(staff?.userId || staff?.user?.id || 0) === currentUserId) ||
        null
    );
}
